import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from "react";
import axios from "axios";
import i18n from "../i18n";
import { loginRestrictionMessage } from "../lib/loginRestriction";

const AuthContext = createContext(null);

// Token lives in HttpOnly cookie — JS never sees it.
// We keep user data in localStorage for instant UI hydration.

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const loggingOut = useRef(false);

  // Send cookies with every request; strip any explicit Authorization headers
  // (the backend middleware reads the HttpOnly cookie instead).
  useEffect(() => {
    axios.defaults.withCredentials = true;
    axios.defaults.timeout = 30000;

    const reqId = axios.interceptors.request.use((config) => {
      if (config.headers) {
        delete config.headers.Authorization;
        delete config.headers.authorization;
      }
      return config;
    });

    return () => axios.interceptors.request.eject(reqId);
  }, []);

  const logout = useCallback(async () => {
    if (loggingOut.current) return;
    loggingOut.current = true;
    try {
      await axios.post("/api/auth/logout");
    } catch {
      // Cookie might already be gone — that's fine
    } finally {
      localStorage.removeItem("sherlock_user");
      setUser(null);
      refreshFailures.current = 0;
      loggingOut.current = false;
    }
  }, []);

  // Sign out of all devices. Unlike logout(), a failure is thrown and this
  // browser stays signed in: clearing it anyway would look like success while
  // every other device kept its session. (A 401 is the exception: the
  // interceptor below signs this browser out, as for any ended session.)
  const logoutAll = useCallback(async () => {
    await axios.post("/api/auth/logout-all");
    localStorage.removeItem("sherlock_user");
    setUser(null);
    refreshFailures.current = 0;
  }, []);

  // Hydrate user from localStorage on mount, then verify from server
  useEffect(() => {
    // Clean up legacy token from localStorage (now in HttpOnly cookie)
    localStorage.removeItem("sherlock_token");

    const storedUser = localStorage.getItem("sherlock_user");
    if (storedUser) {
      try {
        const parsed = JSON.parse(storedUser);
        setUser(parsed);
        // Verify role from server to prevent localStorage tampering
        axios.get("/api/auth/me").then(({ data }) => {
          const verified = { id: data.id, email: data.email, role: data.role, name: data.name };
          setUser(verified);
          localStorage.setItem("sherlock_user", JSON.stringify(verified));
        }).catch(() => {
          // Cookie expired or invalid — clear stale state
          localStorage.removeItem("sherlock_user");
          setUser(null);
        });
      } catch {
        localStorage.removeItem("sherlock_user");
      }
    }
    setLoading(false);
  }, []);

  // Silent token refresh — access token is 15 min; refresh every 10 min.
  // POST /api/auth/refresh reads the httpOnly refresh-token cookie and issues
  // a new access token + rotated refresh token, both as httpOnly cookies.
  const refreshFailures = useRef(0);
  useEffect(() => {
    if (!user) return;
    const REFRESH_INTERVAL = 10 * 60 * 1000; // 10 minutes
    const MAX_FAILURES = 3;
    const interval = setInterval(async () => {
      try {
        await axios.post("/api/auth/refresh", {}, { timeout: 10000 });
        refreshFailures.current = 0;
      } catch (err) {
        if (err.response?.status === 401) return; // interceptor handles logout
        refreshFailures.current += 1;
        if (refreshFailures.current >= MAX_FAILURES) logout();
      }
    }, REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [user, logout]);

  // Intercept 401 responses globally and force re-login.
  // Also intercept 403 with login time restriction to auto-logout.
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (res) => res,
      (err) => {
        if (err.response?.status === 401) logout();
        if (
          err.response?.status === 403 &&
          err.response?.data?.detail === "session_expired_time_restriction"
        ) {
          const msg = loginRestrictionMessage(err.response?.data?.message, i18n.t.bind(i18n));
          // Use a timeout to avoid state update during render
          setTimeout(() => {
            alert(msg);
            logout();
          }, 0);
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, [logout]);

  const login = (userData) => {
    localStorage.setItem("sherlock_user", JSON.stringify(userData));
    setUser(userData);
  };

  const isAdmin = useCallback(() => user?.role === "admin" || user?.role === "super_admin", [user]);
  const isSuperAdmin = useCallback(() => user?.role === "super_admin", [user]);

  // Expose token: null for backward compat with components that destructure it
  const value = useMemo(
    () => ({ user, token: null, login, logout, logoutAll, isAdmin, isSuperAdmin, loading }),
    [user, logout, logoutAll, isAdmin, isSuperAdmin, loading]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
};
